import { DateTime } from "luxon";
import { CalibrationItem } from "./Calibration.types";

const KEY_PREFIX = "aureaCalibrationDraft:";
const MAX_AGE_HOURS = 24;

export type CalibrationDraftMeasurement = Pick<
  CalibrationItem,
  "plotId" | "manualCount"
>;

export type CalibrationDraft = {
  measurements: CalibrationDraftMeasurement[];
  averageWeight: number | null;
  savedAt: string;
};

function draftKey(resultSetId: string): string {
  return `${KEY_PREFIX}${resultSetId}`;
}

function parseDraft(value: string | null): CalibrationDraft | null {
  if (!value) return null;

  try {
    const draft = JSON.parse(value) as CalibrationDraft;
    return draft?.measurements?.length ? draft : null;
  } catch {
    return null;
  }
}

function isExpired(draft: CalibrationDraft | null, expiry: DateTime): boolean {
  const savedAt = draft?.savedAt ? DateTime.fromISO(draft.savedAt) : undefined;
  return !savedAt?.isValid || savedAt.toMillis() < expiry.toMillis();
}

export function readCalibrationDraft(
  resultSetId: string | undefined,
): CalibrationDraft | null {
  if (!resultSetId) return null;

  try {
    const draft = parseDraft(localStorage.getItem(draftKey(resultSetId)));
    const expiry = DateTime.now().minus({ hours: MAX_AGE_HOURS });
    return isExpired(draft, expiry) ? null : draft;
  } catch (error) {
    console.error("Failed to read calibration draft", error);
    return null;
  }
}

export function isDraftStale(
  draft: CalibrationDraft,
  calibratedAt: string | undefined,
): boolean {
  const calibrated = calibratedAt ? DateTime.fromISO(calibratedAt) : undefined;
  if (!calibrated?.isValid) return false;

  return DateTime.fromISO(draft.savedAt).toMillis() < calibrated.toMillis();
}

export function writeCalibrationDraft(
  resultSetId: string | undefined,
  draft: Omit<CalibrationDraft, "savedAt">,
): void {
  if (!resultSetId) return;

  try {
    localStorage.setItem(
      draftKey(resultSetId),
      JSON.stringify({ ...draft, savedAt: DateTime.now().toISO() }),
    );
  } catch (error) {
    console.error("Failed to save calibration draft", error);
  }
}

export function clearCalibrationDraft(resultSetId: string | undefined): void {
  if (!resultSetId) return;

  try {
    localStorage.removeItem(draftKey(resultSetId));
  } catch (error) {
    console.error("Failed to clear calibration draft", error);
  }
}

export function pruneCalibrationDrafts(): void {
  try {
    const expiry = DateTime.now().minus({ hours: MAX_AGE_HOURS });

    Object.keys(localStorage)
      .filter((key) => key.startsWith(KEY_PREFIX))
      .filter((key) => isExpired(parseDraft(localStorage.getItem(key)), expiry))
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to prune calibration drafts", error);
  }
}
